import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import DealerLayout from '../components/DealerLayout';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { C } from '../theme';
import InvoiceModal from '../components/InvoiceModal';

const TYPE_CFG = {
  mapping: { label: 'Sales',   bg: C.primaryBg, color: C.primaryDeep, icon: '📦' },
  renewal: { label: 'Renewal', bg: '#EDE9FE',   color: '#5B21B6',     icon: '🔄' },
  service: { label: 'Service', bg: '#E0F2FE',   color: '#0369A1',     icon: '🛠️' },
};

const OFFLINE_METHODS = ['Cash', 'Cheque', 'Bank Transfer', 'Demand Draft'];
const ONLINE_METHODS  = ['UPI', 'Card', 'Net Banking', 'Bank Transfer'];

// Tabs filter by entry_type (all active shown by default)
const TABS = [
  { key: 'all',     label: 'All Pending' },
  { key: 'mapping', label: 'Sales'       },
  { key: 'renewal', label: 'Renewals'    },
];

const toCollect = (m) =>
  m.entry_type === 'renewal'
    ? (m.total_amc || 0) + (m.total_amount || 0)   // AMC + replacement/additional charges
    : (m.total_amount || 0);

export default function CreditList() {
  const { user } = useAuth();
  const isDealer = user?.role === 'dealer';
  const LayoutComp = isDealer ? DealerLayout : Layout;
  const [mappings,       setMappings]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [tab,            setTab]            = useState('all');
  const [search,         setSearch]         = useState('');
  const [toast,          setToast]          = useState(null);
  const [detailRow,      setDetailRow]      = useState(null);
  const [payHistory,     setPayHistory]     = useState({});
  const [loadingHistory, setLoadingHistory] = useState(null);

  const [payModal,  setPayModal]  = useState(null);
  const [payForm,   setPayForm]   = useState({ mode: 'offline', method: 'Cash', amount: '', notes: '' });
  const [payErr,    setPayErr]    = useState('');
  const [paying,    setPaying]    = useState(false);
  const [invoice,   setInvoice]   = useState(null);

  const [delMap,    setDelMap]    = useState(null);   // mapping to cancel/delete
  const [deleting,  setDeleting]  = useState(false);

  const showToast = (type, text) => { setToast({ type, text }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/product-mappings');
      // Only show active (pending) entries — completed entries disappear from this list
      setMappings(r.data.filter(m => m.status === 'active'));
    }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Computed ────────────────────────────────────────────────────────────────

  const summary = useMemo(() => {
    const salesList   = mappings.filter(m => m.entry_type !== 'renewal');
    const renewalList = mappings.filter(m => m.entry_type === 'renewal');
    const clientSet   = new Set(mappings.map(m => m.client_id));
    return {
      sales_pending:   salesList.reduce((s, m)   => s + (m.balance_due ?? toCollect(m)), 0),
      renewal_pending: renewalList.reduce((s, m) => s + (m.balance_due ?? toCollect(m)), 0),
      total_pending:   mappings.reduce((s, m)    => s + (m.balance_due ?? toCollect(m)), 0),
      client_count:    clientSet.size,
      sales_count:     salesList.length,
      renewal_count:   renewalList.length,
    };
  }, [mappings]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return mappings.filter(m => {
      if (tab === 'mapping' && m.entry_type !== 'mapping') return false;
      if (tab === 'renewal' && m.entry_type !== 'renewal') return false;
      if (!q) return true;
      return [m.client_name, m.client_ref, m.package_ref, m.package_name, m.installer_name]
        .some(v => v?.toLowerCase().includes(q));
    });
  }, [mappings, tab, search]);

  const tabCount = useMemo(() => ({
    all:     mappings.length,
    mapping: mappings.filter(m => m.entry_type !== 'renewal').length,
    renewal: mappings.filter(m => m.entry_type === 'renewal').length,
  }), [mappings]);

  // ── Expand row ───────────────────────────────────────────────────────────────

  const toggleExpand = async (m) => {
    if (detailRow === m.id) { setDetailRow(null); return; }
    setDetailRow(m.id);
    if (!payHistory[m.id]) {
      setLoadingHistory(m.id);
      try {
        const r = await api.get(`/api/product-mappings/${m.id}/payments`);
        setPayHistory(h => ({ ...h, [m.id]: r.data }));
      } catch {} finally { setLoadingHistory(null); }
    }
  };

  // ── Cancel Credit Entry ───────────────────────────────────────────────────

  const cancelCredit = async () => {
    if (!delMap) return;
    setDeleting(true);
    try {
      await api.delete(`/api/product-mappings/${delMap.id}`);
      await load();
      showToast('success', `Credit entry ${delMap.mapping_id} cancelled.`);
      setDelMap(null);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Delete failed.');
    } finally { setDeleting(false); }
  };

  // ── Pay Now ───────────────────────────────────────────────────────────────

  const openPayNow = (m, e) => {
    e.stopPropagation();
    const bal = m.balance_due ?? toCollect(m);
    setPayModal(m);
    setPayForm({ mode: 'offline', method: 'Cash', amount: String(bal), notes: '' });
    setPayErr('');
  };

  const submitPayment = async () => {
    const amt = Number(payForm.amount);
    if (!amt || amt <= 0)                              return setPayErr('Enter a valid amount.');
    if (payForm.mode === 'offline' && !payForm.method) return setPayErr('Select a payment method.');
    const bal = payModal.balance_due ?? toCollect(payModal);
    if (amt > bal + 0.01) return setPayErr(`Amount cannot exceed balance due ₹${bal.toLocaleString('en-IN')}.`);
    setPaying(true); setPayErr('');
    try {
      const r = await api.post('/api/payments', {
        mapping_id:     payModal.id,
        payment_mode:   payForm.mode,
        payment_method: payForm.method || null,
        amount_paid:    amt,
        notes:          payForm.notes || null,
      });
      setPayHistory(h => { const n = { ...h }; delete n[payModal.id]; return n; });
      await load();        // reload — completed entries will disappear automatically
      setPayModal(null);
      setInvoice(r.data); // open invoice immediately
      // Receipt email is auto-sent from the business SMTP — surface the outcome
      if (r.data?.email_sent) {
        showToast('success', `Payment recorded · receipt emailed to ${r.data.client_email} ✓`);
      } else if (r.data?.client_email && r.data?.email_error) {
        showToast('error', `Payment recorded, but email failed: ${r.data.email_error}`);
      } else {
        showToast('success', 'Payment recorded.');
      }
    } catch (e) {
      setPayErr(e?.response?.data?.detail || 'Payment failed.');
    } finally { setPaying(false); }
  };

  const isPartial = (m) => (m.is_partial) || ((m.amount_paid || 0) > 0 && (m.balance_due ?? 0) > 0);

  return (
    <LayoutComp title="Credit List">

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 5000,
          background: toast.type === 'success' ? '#D1FAE5' : '#FEE2E2',
          color:      toast.type === 'success' ? '#065F46' : '#991B1B',
          border:     `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', maxWidth: '380px',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* ── Summary Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))', gap: '14px', marginBottom: '22px' }}>
        <SCard icon="📦" label="Sales Pending"    value={`₹${summary.sales_pending.toLocaleString('en-IN')}`}   sub={`${summary.sales_count} entries`}   dot={C.primary} bg={C.primaryBg} color={C.primaryDeep} />
        <SCard icon="🔄" label="Renewal Pending"  value={`₹${summary.renewal_pending.toLocaleString('en-IN')}`} sub={`${summary.renewal_count} entries`} dot="#8B5CF6"   bg="#EDE9FE"    color="#5B21B6"      />
        <SCard icon="💰" label="Total to Collect" value={`₹${summary.total_pending.toLocaleString('en-IN')}`}   sub="combined pending balance"           dot={C.danger}  bg="#FEE2E2"    color="#991B1B"      />
        <SCard icon="👥" label="Clients"          value={summary.client_count}                                   sub={`${mappings.length} pending entries`} dot={C.success} bg={C.successBg} color={C.successText} />
      </div>

      {/* ── Table Card ── */}
      <div style={{ background: C.white, borderRadius: '14px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>

        {/* Tabs + Search */}
        <div style={{ padding: '16px 22px', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '4px', background: '#F3F4F6', borderRadius: '10px', padding: '4px' }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} style={{
                padding: '6px 14px', borderRadius: '7px', fontSize: '12px', fontWeight: 700,
                border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                background: tab === t.key ? C.white : 'transparent',
                color:      tab === t.key ? C.textMain : C.textSub,
                boxShadow:  tab === t.key ? '0 1px 4px rgba(0,0,0,0.12)' : 'none',
              }}>
                {t.label}
                {(tabCount[t.key] || 0) > 0 && (
                  <span style={{
                    marginLeft: '6px', padding: '1px 6px', borderRadius: '20px', fontSize: '10px',
                    background: tab === t.key ? (t.key === 'renewal' ? '#EDE9FE' : C.primaryBg) : '#E5E7EB',
                    color:      tab === t.key ? (t.key === 'renewal' ? '#5B21B6'  : C.primaryDeep) : C.textSub,
                  }}>{tabCount[t.key] || 0}</span>
                )}
              </button>
            ))}
          </div>
          <input placeholder="Search client, package…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '8px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' }} />
          {search && <button onClick={() => setSearch('')} style={{ padding: '8px 14px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>Clear</button>}
          <span style={{ fontSize: '12px', color: C.textSub, whiteSpace: 'nowrap' }}>{filtered.length} pending</span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '860px' }}>
            <thead>
              <tr style={{ background: C.primaryBg }}>
                {['Client', 'Type', 'Package', 'Installer', 'Balance Due', 'Date', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '11px 16px', fontSize: '11px', fontWeight: 700, color: C.textSub, textAlign: 'left', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: `2px solid ${C.border}`, whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '60px', color: C.textSub }}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '60px' }}>
                  <div style={{ fontSize: '36px', marginBottom: '10px' }}>✅</div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: C.textMain }}>
                    {mappings.length === 0 ? 'No pending entries' : 'No entries match the filter'}
                  </div>
                  <div style={{ fontSize: '12px', color: C.textSub, marginTop: '4px' }}>
                    {mappings.length === 0 ? 'All payments are cleared. Completed entries are moved to Sales / Renewal History.' : ''}
                  </div>
                </td></tr>
              ) : filtered.map((m, i) => {
                const tc      = TYPE_CFG[m.entry_type] || TYPE_CFG.mapping;
                const bal     = m.balance_due ?? toCollect(m);
                const partial = isPartial(m);
                const isExp   = detailRow === m.id;

                return (
                  <React.Fragment key={m.id}>
                    <tr onClick={() => toggleExpand(m)} style={{
                      background: i % 2 === 0 ? '#FFFDF5' : '#FFFBEB',
                      borderBottom: `1px solid ${C.border}`, cursor: 'pointer',
                    }}>
                      {/* Client */}
                      <td style={{ padding: '13px 16px', verticalAlign: 'middle' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0, background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 800, color: '#fff' }}>
                            {(m.client_name || 'C')[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: '13px', color: C.textMain }}>{m.client_name}</div>
                            <div style={{ fontSize: '11px', color: C.textSub }}>{m.client_ref}</div>
                          </div>
                        </div>
                      </td>
                      {/* Type */}
                      <td style={{ padding: '13px 16px', verticalAlign: 'middle' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: tc.bg, color: tc.color, padding: '3px 9px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>
                          {tc.icon} {tc.label}
                        </span>
                      </td>
                      {/* Package */}
                      <td style={{ padding: '13px 16px', verticalAlign: 'middle' }}>
                        <div style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: '#7C3AED' }}>{m.package_ref}</div>
                        {m.package_name && <div style={{ fontSize: '11px', color: C.textSub }}>{m.package_name}</div>}
                      </td>
                      {/* Installer */}
                      <td style={{ padding: '13px 16px', verticalAlign: 'middle' }}>
                        <div style={{ fontSize: '13px', color: C.textMain }}>{m.installer_name}</div>
                        <div style={{ fontSize: '11px', color: C.textSub }}>@{m.installer_user}</div>
                      </td>
                      {/* Balance Due */}
                      <td style={{ padding: '13px 16px', verticalAlign: 'middle' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: '#FEF3C7', color: '#92400E', padding: '5px 11px', borderRadius: '8px', fontWeight: 800, fontSize: '14px' }}>
                          ₹{bal.toLocaleString('en-IN')}
                        </div>
                        {partial && (
                          <div style={{ fontSize: '10px', color: '#92400E', marginTop: '3px', fontWeight: 600 }}>
                            Partial · ₹{(m.amount_paid || 0).toLocaleString('en-IN')} paid
                          </div>
                        )}
                        <div style={{ fontSize: '10px', color: C.textSub, marginTop: '2px' }}>
                          {m.entry_type === 'renewal'
                            ? ((m.total_amount || 0) > 0 ? 'AMC + Charges' : 'AMC only')
                            : 'Amount only'}
                        </div>
                      </td>
                      {/* Date */}
                      <td style={{ padding: '13px 16px', verticalAlign: 'middle', fontSize: '12px', color: C.textSub }}>{m.created_at}</td>
                      {/* Actions */}
                      <td style={{ padding: '13px 16px', verticalAlign: 'middle' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'stretch', minWidth: '110px' }}>
                          <button onClick={(e) => openPayNow(m, e)} style={{
                            padding: '7px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, border: 'none',
                            background: m.entry_type === 'renewal'
                              ? 'linear-gradient(135deg, #7C3AED, #5B21B6)'
                              : `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
                            color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap',
                            boxShadow: m.entry_type === 'renewal'
                              ? '0 2px 8px rgba(124,58,237,0.35)'
                              : `0 2px 8px rgba(245,166,35,0.35)`,
                          }}>
                            💳 Pay Now
                          </button>
                          {m.entry_type === 'renewal' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setDelMap(m); }}
                              style={{
                                padding: '5px 14px', borderRadius: '8px', fontSize: '11px', fontWeight: 600,
                                background: '#fff', color: '#DC2626',
                                border: '1.5px solid #FECACA',
                                cursor: 'pointer', whiteSpace: 'nowrap',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                              }}
                            >
                              <span style={{ fontSize: '13px', lineHeight: 1 }}>×</span> Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded detail + payment history */}
                    {isExp && (
                      <tr style={{ background: '#FAFBFF', borderBottom: `2px solid ${C.primary}30` }}>
                        <td colSpan={7} style={{ padding: '0' }}>
                          <div style={{ padding: '16px 24px' }}>
                            {/* Info row */}
                            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '16px' }}>
                              <DItem icon="🆔" label="Mapping ID" value={m.mapping_id} mono />
                              {m.client_contact && <DItem icon="📞" label="Contact" value={m.client_contact} />}
                              {m.client_email   && <DItem icon="✉️"  label="Email"   value={m.client_email} />}
                              {m.notes && <DItem icon="📝" label="Notes" value={m.notes} />}
                              {/* Breakdown */}
                              <div style={{ marginLeft: 'auto', background: '#F8FAFF', border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 16px', minWidth: '200px' }}>
                                <div style={{ fontSize: '10px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Breakdown</div>
                                {m.entry_type !== 'renewal'
                                  ? <><BLine label="Amount (to collect)" val={m.total_amount} color={C.primaryDeep} active />
                                       {m.total_amc > 0 && <BLine label="AMC" val={m.total_amc} color={C.textSub} dim />}</>
                                  : <><BLine label="AMC (to collect)" val={m.total_amc} color="#5B21B6" active />
                                       {(m.total_amount || 0) > 0 && <BLine label="Replacement / Additional" val={m.total_amount} color={C.primaryDeep} active />}</>
                                }
                                <div style={{ borderTop: `1px dashed ${C.border}`, margin: '6px 0' }} />
                                {(m.amount_paid || 0) > 0 && <BLine label="Paid so far" val={m.amount_paid} color="#065F46" />}
                                <BLine label="Balance Due" val={bal} color="#92400E" bold />
                              </div>
                            </div>

                            {/* Payment History */}
                            <div>
                              <div style={{ fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>
                                Payment History
                              </div>
                              {loadingHistory === m.id ? (
                                <div style={{ fontSize: '12px', color: C.textSub }}>Loading…</div>
                              ) : !payHistory[m.id] || payHistory[m.id].length === 0 ? (
                                <div style={{ fontSize: '12px', color: C.textSub, fontStyle: 'italic' }}>No payments recorded yet.</div>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  {payHistory[m.id].map(p => (
                                    <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '16px', background: '#ECFDF5', border: '1px solid #6EE7B7', borderRadius: '8px', padding: '10px 14px', flexWrap: 'wrap' }}>
                                      <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 800, color: '#065F46' }}>{p.payment_id}</span>
                                      <span style={{ fontSize: '15px', fontWeight: 900, color: '#065F46' }}>₹{(p.amount_paid || 0).toLocaleString('en-IN')}</span>
                                      <ModeBadge mode={p.payment_mode} method={p.payment_method} />
                                      <span style={{ fontSize: '11px', color: C.textSub }}>{p.created_at}</span>
                                      {p.balance_due > 0 && <span style={{ fontSize: '11px', color: '#92400E', fontWeight: 600 }}>Balance: ₹{p.balance_due.toLocaleString('en-IN')}</span>}
                                      {p.notes && <span style={{ fontSize: '11px', color: C.textSub, fontStyle: 'italic' }}>"{p.notes}"</span>}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        {filtered.length > 0 && (
          <div style={{ padding: '14px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'center', background: '#FAFAFA' }}>
            <span style={{ fontSize: '12px', color: C.textSub, fontWeight: 600 }}>SHOWING {filtered.length} ENTRIES</span>
            <span style={{ color: C.textSub }}>|</span>
            <FTotal label="Sales Balance"   val={filtered.filter(m => m.entry_type !== 'renewal').reduce((s, m) => s + (m.balance_due ?? toCollect(m)), 0)} color={C.primaryDeep} />
            <FTotal label="Renewal Balance" val={filtered.filter(m => m.entry_type === 'renewal').reduce((s, m) => s + (m.balance_due ?? toCollect(m)), 0)} color="#5B21B6" />
            <FTotal label="Total"           val={filtered.reduce((s, m) => s + (m.balance_due ?? toCollect(m)), 0)} color={C.danger} bold />
          </div>
        )}
      </div>

      {/* ── Pay Now Modal ── */}
      {payModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '18px', width: '100%', maxWidth: '500px', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}`, maxHeight: '92vh', overflowY: 'auto' }}>

            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '3px', height: '20px', background: payModal.entry_type === 'renewal' ? '#7C3AED' : C.primary, borderRadius: '2px' }} />
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>
                    {payModal.entry_type === 'renewal' ? '🔄 Renewal Payment' : '💳 Pay Now'}
                  </div>
                  <div style={{ fontSize: '11px', color: C.textSub, marginTop: '1px' }}>
                    {payModal.client_name} · {payModal.mapping_id}
                  </div>
                </div>
              </div>
              <button onClick={() => setPayModal(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {payErr && (
                <div style={{ background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px' }}>
                  ⚠ {payErr}
                </div>
              )}

              {/* Amount summary */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '20px' }}>
                {[
                  { label: 'Total Due',   val: toCollect(payModal), color: C.textMain,  bg: '#F9FAFB' },
                  { label: 'Paid So Far', val: payModal.amount_paid || 0, color: '#065F46', bg: '#ECFDF5' },
                  { label: 'Balance Due', val: payModal.balance_due ?? toCollect(payModal), color: '#92400E', bg: '#FEF3C7' },
                ].map(({ label, val, color, bg }) => (
                  <div key={label} style={{ background: bg, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '16px', fontWeight: 900, color }}>₹{(val || 0).toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: '10px', color: C.textSub, marginTop: '3px', fontWeight: 600, textTransform: 'uppercase' }}>{label}</div>
                  </div>
                ))}
              </div>

              {/* Payment Mode */}
              <div style={{ marginBottom: '18px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '8px' }}>Payment Mode</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['offline', 'online'].map(mode => (
                    <button key={mode} onClick={() => setPayForm(f => ({ ...f, mode, method: mode === 'offline' ? 'Cash' : 'UPI' }))}
                      style={{
                        flex: 1, padding: '10px', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
                        background: payForm.mode === mode ? (mode === 'offline' ? C.primary : '#7C3AED') : '#F3F4F6',
                        color:      payForm.mode === mode ? '#fff' : C.textSub,
                        border:     `2px solid ${payForm.mode === mode ? (mode === 'offline' ? C.primary : '#7C3AED') : C.border}`,
                      }}>
                      {mode === 'offline' ? '💵 Offline' : '🌐 Online'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Payment Method */}
              <div style={{ marginBottom: '18px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '8px' }}>
                  Payment Method
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {(payForm.mode === 'offline' ? OFFLINE_METHODS : ONLINE_METHODS).map(method => (
                    <button key={method} onClick={() => setPayForm(f => ({ ...f, method }))}
                      style={{
                        padding: '7px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                        background: payForm.method === method ? (payForm.mode === 'offline' ? C.primaryBg : '#EDE9FE') : '#F3F4F6',
                        color:      payForm.method === method ? (payForm.mode === 'offline' ? C.primaryDeep : '#5B21B6') : C.textSub,
                        border:     `1.5px solid ${payForm.method === method ? (payForm.mode === 'offline' ? C.primary : '#8B5CF6') : C.border}`,
                      }}>
                      {method}
                    </button>
                  ))}
                </div>
              </div>

              {/* Amount */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: C.textMain }}>Amount (₹) *</span>
                  {Number(payForm.amount) < (payModal.balance_due ?? toCollect(payModal)) - 0.01 && Number(payForm.amount) > 0 && (
                    <span style={{ fontSize: '11px', background: '#FEF3C7', color: '#92400E', padding: '2px 8px', borderRadius: '20px', fontWeight: 600 }}>
                      Partial payment · balance remains
                    </span>
                  )}
                </div>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontWeight: 700, color: C.textSub }}>₹</span>
                  <input
                    type="number" min="0.01" step="0.01"
                    max={payModal.balance_due ?? toCollect(payModal)}
                    value={payForm.amount}
                    onChange={e => setPayForm(f => ({ ...f, amount: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px 10px 28px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '16px', fontWeight: 700, outline: 'none', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  <button onClick={() => setPayForm(f => ({ ...f, amount: String(payModal.balance_due ?? toCollect(payModal)) }))}
                    style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px', background: C.primaryBg, color: C.primaryDeep, border: `1px solid ${C.border}`, cursor: 'pointer', fontWeight: 600 }}>
                    Full amount
                  </button>
                  {[25, 50, 75].map(pct => {
                    const b = payModal.balance_due ?? toCollect(payModal);
                    return (
                      <button key={pct} onClick={() => setPayForm(f => ({ ...f, amount: String(Math.round(b * pct / 100)) }))}
                        style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px', background: '#F3F4F6', color: C.textSub, border: `1px solid ${C.border}`, cursor: 'pointer', fontWeight: 600 }}>
                        {pct}%
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Notes */}
              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>Notes (optional)</div>
                <textarea value={payForm.notes} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))}
                  rows={2} placeholder="Cheque no., transaction ID, remarks…"
                  style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setPayModal(null)} disabled={paying}
                style={{ padding: '10px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={submitPayment} disabled={paying}
                style={{
                  padding: '10px 24px',
                  background: payModal.entry_type === 'renewal'
                    ? 'linear-gradient(135deg, #7C3AED, #5B21B6)'
                    : `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
                  color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700,
                  cursor: paying ? 'not-allowed' : 'pointer', opacity: paying ? 0.7 : 1,
                  boxShadow: payModal.entry_type === 'renewal' ? '0 3px 10px rgba(124,58,237,0.3)' : `0 3px 10px rgba(245,166,35,0.3)`,
                }}>
                {paying ? 'Processing…' : '✓ Confirm Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      <InvoiceModal payment={invoice} onClose={() => setInvoice(null)} canSend={!isDealer} />

      {/* Cancel Credit Confirm Modal */}
      {delMap && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: '16px' }}>
          <div style={{ background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', border: `1px solid ${C.border}` }}>
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '3px', height: '18px', background: C.danger, borderRadius: '2px' }} />
                <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>Cancel Credit Entry</span>
              </div>
              <button onClick={() => setDelMap(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: '10px', padding: '14px', fontSize: '13px', color: '#92400E', lineHeight: 1.6 }}>
                ⚠ Cancel credit entry <strong style={{ fontFamily: 'monospace' }}>{delMap.mapping_id}</strong> for <strong>{delMap.client_name}</strong>?
                <br />
                <span style={{ fontSize: '12px', marginTop: '6px', display: 'block' }}>
                  This removes the ₹{(delMap.balance_due ?? toCollect(delMap)).toLocaleString('en-IN')} pending charge. The renewal subscription will go back to showing <strong>Renew Now</strong>.
                </span>
              </div>
            </div>
            <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setDelMap(null)} disabled={deleting} style={{ padding: '9px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
                Keep Entry
              </button>
              <button onClick={cancelCredit} disabled={deleting} style={{ padding: '9px 18px', background: C.danger, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>
                {deleting ? 'Cancelling…' : 'Yes, Cancel It'}
              </button>
            </div>
          </div>
        </div>
      )}
    </LayoutComp>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SCard({ icon, label, value, sub, dot, bg, color }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '18px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
        <span style={{ fontSize: '22px' }}>{icon}</span>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot, marginTop: '4px' }} />
      </div>
      <div style={{ fontSize: '22px', fontWeight: 800, color: C.textMain, marginBottom: '4px' }}>{value}</div>
      <div style={{ fontSize: '12px', fontWeight: 700, color, background: bg, display: 'inline-block', padding: '2px 8px', borderRadius: '6px', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '11px', color: C.textSub }}>{sub}</div>
    </div>
  );
}

function DItem({ icon, label, value, mono }) {
  return (
    <div>
      <div style={{ fontSize: '10px', color: C.textSub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '3px' }}>{icon} {label}</div>
      <div style={{ fontSize: '13px', fontWeight: 600, color: C.textMain, fontFamily: mono ? 'monospace' : 'inherit' }}>{value}</div>
    </div>
  );
}

function BLine({ label, val, color, bold, dim, active }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '4px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        {active && <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: color, flexShrink: 0 }} />}
        <span style={{ fontSize: '11px', color: dim ? C.textSub : C.textMain, fontStyle: dim ? 'italic' : 'normal' }}>{label}</span>
      </div>
      <span style={{ fontSize: bold ? '14px' : '11px', fontWeight: bold ? 800 : 600, color: dim ? C.textSub : color, textDecoration: dim ? 'line-through' : 'none', whiteSpace: 'nowrap' }}>
        ₹{(val || 0).toLocaleString('en-IN')}
      </span>
    </div>
  );
}

function ModeBadge({ mode, method }) {
  const isOnline = mode === 'online';
  return (
    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
      <span style={{ background: isOnline ? '#EDE9FE' : '#FEF3C7', color: isOnline ? '#5B21B6' : '#92400E', padding: '2px 8px', borderRadius: '20px', fontSize: '10px', fontWeight: 700 }}>
        {isOnline ? '🌐 Online' : '💵 Offline'}
      </span>
      {method && <span style={{ fontSize: '11px', color: C.textSub, fontWeight: 500 }}>{method}</span>}
    </div>
  );
}

function FTotal({ label, val, color, bold }) {
  return (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
      <span style={{ fontSize: '12px', color: C.textSub }}>{label}:</span>
      <span style={{ fontSize: bold ? '14px' : '13px', fontWeight: bold ? 800 : 700, color }}>
        ₹{(val || 0).toLocaleString('en-IN')}
      </span>
    </div>
  );
}
